import { z } from 'zod';

/**
 * `pending-release-readiness` v0 — wire types for the one-URL
 * release-verdict feature.
 *
 * Design doc: docs/handbook/features/pending/release-readiness.md
 * Locked decisions: 2026-05-28.
 *
 * v0 surface: 5 tiles (Tests, TIA, Flake budget, Visual diff,
 * Prod error rate). New-code coverage / a11y delta / perf delta
 * land in v0.1 once their input services are wired. Tiles whose
 * input is unconfigured render as `n/a` and are excluded from
 * verdict math.
 */

export const RELEASE_VERDICTS = ['green', 'amber', 'red'] as const;
export type ReleaseVerdict = (typeof RELEASE_VERDICTS)[number];

/**
 * `n/a` is a first-class state — see locked decision §3
 * (n/a tiles are excluded from verdict math, not coerced to amber).
 */
export const RELEASE_TILE_STATES = ['green', 'amber', 'red', 'na'] as const;
export type ReleaseTileState = (typeof RELEASE_TILE_STATES)[number];

// `pending-revenue-weighted-coverage` v0.1 — adds the `revenue_weighted`
// tile. The composer reads `test_cases.revenue_tier` (migration 120)
// for in-scope flows + the latest test_run per flow on the commit
// being scored, then sums via `computeRevenueWeightedCoverage()`.
// Empty-org case (zero tagged flows) renders as `na` so we don't drag
// the verdict to amber on first install. Locked decision §3: the tile
// detail surfaces `untaggedFlows` so operators see the denominator
// is incomplete.
export const RELEASE_TILE_KINDS = [
  'tests',
  'tia',
  'flake',
  'visual_diff',
  'prod_error_rate',
  'revenue_weighted',
  'risks',
  'failures',
] as const;
export type ReleaseTileKind = (typeof RELEASE_TILE_KINDS)[number];

export const ReleaseTileSchema = z.object({
  kind: z.enum(RELEASE_TILE_KINDS),
  state: z.enum(RELEASE_TILE_STATES),
  /** Headline number rendered in the tile. `null` when state is `na`. */
  headline: z.string().nullable(),
  /** Compact human-readable single-line explanation. */
  detail: z.string(),
  /** Optional 14-point sparkline of the underlying metric. */
  sparkline: z.array(z.number()).optional(),
  /** Deep-link URL the tile chevron routes to. */
  drilldownUrl: z.string().nullable(),
  /** Extra payload — kind-specific. Used by the SPA for hover detail. */
  data: z.record(z.unknown()).optional(),
});
export type ReleaseTile = z.infer<typeof ReleaseTileSchema>;

export const ReleaseVerdictPayloadSchema = z.object({
  projectId: z.string(),
  projectSlug: z.string(),
  commitSha: z.string(),
  /**
   * Verdict computed over the configured (non-na) tiles. See
   * locked decision §7 — single-red-tile flips, no weighting in v0.
   */
  verdict: z.enum(RELEASE_VERDICTS),
  /** 2-sentence LLM-authored explanation of the verdict. */
  llmSummary: z.string().nullable(),
  /**
   * Provider that authored the summary — surfaced for the
   * "powered by" UX next to the LLM blob.
   */
  llmProvider: z.string().nullable(),
  tiles: z.array(ReleaseTileSchema),
  /** When the cached row was computed (matches DB `computed_at`). */
  computedAt: z.string(),
  /** Whether this response was served from cache or freshly computed. */
  fromCache: z.boolean(),
  /** When the cached row will be invalidated. */
  cacheExpiresAt: z.string(),
});
export type ReleaseVerdictPayload = z.infer<typeof ReleaseVerdictPayloadSchema>;

export const ReleaseVerdictResponseSchema = ReleaseVerdictPayloadSchema;
export type ReleaseVerdictResponse = z.infer<typeof ReleaseVerdictResponseSchema>;

export const RELEASE_READINESS_AUDIT_ACTIONS = [
  'RELEASE_VERDICT_COMPUTED',
  'RELEASE_VERDICT_REFRESHED',
  'RELEASE_SHARE_LINK_CREATED',
] as const;
export type ReleaseReadinessAuditAction = (typeof RELEASE_READINESS_AUDIT_ACTIONS)[number];
